/**
 * useHomeARDemoInvoicePageDetailLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait HomeARDemoInvoicePageDetail
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { emitCascadeEvent, useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, HomeARDemoInvoicePageItem, HomeARDemoInvoicePageDetailEventPayloadMap } from '@app/shared';
import { useUIEvents, claimDelivery } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';
import type { BusEvent } from '@almadar/core';
import { useTranslate } from '@almadar/ui/hooks';

// ============================================================================
// State Machine
// ============================================================================

export type HomeARDemoInvoicePageDetailState =
  | 'idle'
  | 'loading'
  | 'editing'
  | 'viewing'
  | 'error';

export type HomeARDemoInvoicePageDetailEvent =
  | 'INIT'
  | 'CONTENT_SAVED'
  | 'CONTENT_COMMITTED'
  | 'TITLE_COMMITTED'
  | 'OPEN_VIEW'
  | 'RecordItemLoaded'
  | 'RecordItemSlugLoaded'
  | 'RecordItemLoadFailed'
  | 'CONTENT_CHANGE'
  | 'DONE_EDITING'
  | 'CLOSE_VIEW'
  | 'EDIT_CONTENT'
  | 'RETRY'
  | 'EDIT'
  | 'DELETE'
  | 'OPEN_INVOICE'
  | 'VIEW';

type HomeARDemoInvoicePageDetailSlot = 'main';
interface HomeARDemoInvoicePageDetailInternalState {
  machineState: HomeARDemoInvoicePageDetailState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: HomeARDemoInvoicePageDetailEvent | null;
  slotWriter: Partial<Record<HomeARDemoInvoicePageDetailSlot, string | null>>;
  loading: boolean;
  pendingEvent: HomeARDemoInvoicePageDetailEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeARDemoInvoicePageItem>;
}

type HomeARDemoInvoicePageDetailAction =
  | { type: 'EVENT_SUCCESS'; event: HomeARDemoInvoicePageDetailEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: HomeARDemoInvoicePageDetailSlot[]; renderClear?: HomeARDemoInvoicePageDetailSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: HomeARDemoInvoicePageDetailEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof HomeARDemoInvoicePageItem & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function HomeARDemoInvoicePageDetailRenderWriter(event: string, newState: string): string {
  return ['RecordItemLoaded', 'RecordItemSlugLoaded'].includes(event) ? `${event}>${newState}` : event;
}

function homeARDemoInvoicePageDetailReducer(
  state: HomeARDemoInvoicePageDetailInternalState,
  action: HomeARDemoInvoicePageDetailAction
): HomeARDemoInvoicePageDetailInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as HomeARDemoInvoicePageDetailState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, HomeARDemoInvoicePageDetailRenderWriter(action.event, action.newState)] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<HomeARDemoInvoicePageItem>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<HomeARDemoInvoicePageItem>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<HomeARDemoInvoicePageItem>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: HomeARDemoInvoicePageDetailInternalState = {
  machineState: 'idle',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

import type { SExpr } from '@almadar/core';

export interface HomeARDemoInvoicePageDetailConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  actions: { event: string; icon?: string; label: string; roles?: string[]; variant?: 'primary' | 'secondary' | 'ghost' | 'danger'; when?: SExpr }[];
  allowEdit: boolean;
  autosaveHint: string;
  avatarField: string;
  bodyContent: { children: { actions: Array<Array<Array<Array<Array<Array<Array<FieldValue[] | string> | string> | number | string> | Array<Array<FieldValue[] | string> | string> | string> | string> | string> | FieldValue[] | string>; avatarField: string; closeEvent: string; coverField: string; entity: string; fields: string; initialData: string; latitudeField: string; lineItemColumns: string; lineItems: string[]; longitudeField: string; look: string; maxInlineActions: string; mediaField: string; sections: string; showActions: string; stageField: string; stages: string; subtitle: string; tabs: string; thread: string[]; title: string; totals: string; type: string }[]; direction: string; gap: string; type: string };
  closeEvent: string;
  contentField: string;
  coverField: string;
  detailLook: 'panel' | 'slide-over' | 'page' | 'document';
  documentBodyContent: { actions: Array<Array<Array<Array<Array<Array<FieldValue[] | string> | string> | number | string> | Array<Array<FieldValue[] | string> | string> | string> | string> | string>; className: string; coverImage: string; editEvent: Array<null | string>; editLabel: string; icon: string; id: string; maxInlineActions: string; placeholder: string; subtitle: string; title: string; titleCommitEvent: Array<null | string>; type: string; value: string };
  documentEditingContent: { actions: Array<Array<Array<Array<Array<Array<FieldValue[] | string> | string> | number | string> | Array<Array<FieldValue[] | string> | string> | string> | string> | string>; autosaveHint: string; className: string; contentChangeEvent: string; coverImage: string; doneEvent: string; doneLabel: string; editing: boolean; icon: string; id: string; maxInlineActions: string; placeholder: string; subtitle: string; title: string; titleCommitEvent: string; type: string; value: string };
  doneLabel: string;
  editLabel: string;
  fields: Array<{ colorMap: { draft: string; overdue: string; paid: string; sent: string }; header: string[]; key: string; labels: { draft: string[]; overdue: string[]; paid: string[]; sent: string[] }; variant: string } | { format: string; header: string[]; key: string }>;
  iconField: string;
  include: string[];
  latitudeField: string;
  lineItemColumns: Array<{ align: string; format: string; header: string[]; key: string } | { header: string[]; key: string }>;
  lineItemsField: string;
  loadingContent: { type: string; variant: string };
  longitudeField: string;
  maxInlineActions: number;
  mediaField: string;
  openOn: 'init' | 'event';
  pageBodyContent: { children: { actions: Array<Array<Array<Array<Array<Array<Array<FieldValue[] | string> | string> | number | string> | Array<Array<FieldValue[] | string> | string> | string> | string> | string> | FieldValue[] | string>; avatarField: string; closeEvent: string; coverField: string; entity: string; fields: string; initialData: string; latitudeField: string; lineItemColumns: string; lineItems: string[]; longitudeField: string; look: string; maxInlineActions: string; mediaField: string; sections: string; showActions: string; stageField: string; stages: string; subtitle: string; tabs: string; thread: string[]; title: string; totals: string; type: string }[]; className: string; direction: string; gap: string; type: string };
  placeholder: string;
  recordLook: 'panel' | 'profile' | 'showcase' | 'workflow' | 'map' | 'ledger' | 'conversation' | 'workspace';
  sections: { fields?: string[]; title: string }[];
  showActions: boolean;
  slideOverBodyContent: { children: { actions: Array<Array<Array<Array<Array<Array<Array<FieldValue[] | string> | string> | number | string> | Array<Array<FieldValue[] | string> | string> | string> | string> | string> | FieldValue[] | string>; avatarField: string; closeEvent: string; coverField: string; entity: string; fields: string; initialData: string; latitudeField: string; lineItemColumns: string; lineItems: string[]; longitudeField: string; look: string; maxInlineActions: string; mediaField: string; sections: string; showActions: string; slideOver: boolean; stageField: string; stages: string; subtitle: string; tabs: string; thread: string[]; title: string; totals: string; type: string }[]; direction: string; gap: string; type: string };
  stageField: string;
  stages: { label: string; value: string }[];
  subtitleField: string;
  tabs: { content?: string; fields?: string[]; id: string; label: string }[];
  threadField: string;
  titleField: string;
  titleFields: string[];
  totals: { format: string; header: string[]; key: string }[];
  viewerRole: string;
}

const DEFAULT_HOME_AR_DEMO_INVOICE_PAGE_DETAIL_CONFIG = {
  actions: [],
  allowEdit: true,
  autosaveHint: "Saves as you type",
  avatarField: "",
  bodyContent: { children: [{ actions: ["if", "@config.showActions", ["array/filter", "@config.actions", ["fn", "action", ["or", ["=", ["array/len", ["object/get", "@action", "roles", []]], 0], ["array/includes", ["object/get", "@action", "roles", []], "@config.viewerRole"]]]], []], avatarField: "@config.avatarField", closeEvent: "@config.closeEvent", coverField: "@config.coverField", entity: "@entity.loadedRow", fields: "@config.fields", initialData: "@entity.loadedRow", latitudeField: "@config.latitudeField", lineItemColumns: "@config.lineItemColumns", lineItems: ["object/get", "@entity.loadedRow", "@config.lineItemsField"], longitudeField: "@config.longitudeField", look: "@config.recordLook", maxInlineActions: "@config.maxInlineActions", mediaField: "@config.mediaField", sections: "@config.sections", showActions: "@config.showActions", stageField: "@config.stageField", stages: "@config.stages", subtitle: "@entity.description", tabs: "@config.tabs", thread: ["object/get", "@entity.loadedRow", "@config.threadField"], title: "@entity.name", totals: "@config.totals", type: "detail-panel" }], direction: "vertical", gap: "lg", type: "stack" },
  closeEvent: "",
  contentField: "content",
  coverField: "",
  detailLook: "panel",
  documentBodyContent: { actions: ["array/filter", "@config.actions", ["fn", "action", ["or", ["=", ["array/len", ["object/get", "@action", "roles", []]], 0], ["array/includes", ["object/get", "@action", "roles", []], "@config.viewerRole"]]]], className: "w-full", coverImage: "@entity.coverUrl", editEvent: ["if", "@config.allowEdit", "EDIT_CONTENT", null], editLabel: "@config.editLabel", icon: "@entity.iconName", id: "@entity.id", maxInlineActions: "@config.maxInlineActions", placeholder: "@config.placeholder", subtitle: "@entity.description", title: "@entity.name", titleCommitEvent: ["if", "@config.allowEdit", "TITLE_COMMITTED", null], type: "document-panel", value: "@entity.content" },
  documentEditingContent: { actions: ["array/filter", "@config.actions", ["fn", "action", ["or", ["=", ["array/len", ["object/get", "@action", "roles", []]], 0], ["array/includes", ["object/get", "@action", "roles", []], "@config.viewerRole"]]]], autosaveHint: "@config.autosaveHint", className: "w-full", contentChangeEvent: "CONTENT_CHANGE", coverImage: "@entity.coverUrl", doneEvent: "DONE_EDITING", doneLabel: "@config.doneLabel", editing: true, icon: "@entity.iconName", id: "@entity.id", maxInlineActions: "@config.maxInlineActions", placeholder: "@config.placeholder", subtitle: "@entity.description", title: "@entity.name", titleCommitEvent: "TITLE_COMMITTED", type: "document-panel", value: "@entity.content" },
  doneLabel: "Done",
  editLabel: "Edit",
  fields: [{ colorMap: { draft: "default", overdue: "error", paid: "success", sent: "info" }, header: ["i18n/t", "std-almadar-demo-invoices-page:invoice.status"], key: "status", labels: { draft: ["i18n/t", "std-almadar-demo-invoices-page:status.draft"], overdue: ["i18n/t", "std-almadar-demo-invoices-page:status.overdue"], paid: ["i18n/t", "std-almadar-demo-invoices-page:status.paid"], sent: ["i18n/t", "std-almadar-demo-invoices-page:status.sent"] }, variant: "badge" }, { format: "date", header: ["i18n/t", "std-almadar-demo-invoices-page:invoice.due"], key: "dueDate" }],
  iconField: "",
  include: [],
  latitudeField: "",
  lineItemColumns: [{ header: ["i18n/t", "std-almadar-demo-invoices-page:line.item"], key: "description" }, { align: "right", format: "number", header: ["i18n/t", "std-almadar-demo-invoices-page:line.qty"], key: "quantity" }, { align: "right", format: "currency", header: ["i18n/t", "std-almadar-demo-invoices-page:line.unit"], key: "unitPrice" }, { align: "right", format: "currency", header: ["i18n/t", "std-almadar-demo-invoices-page:line.amount"], key: "amount" }],
  lineItemsField: "lines",
  loadingContent: { type: "skeleton", variant: "detail" },
  longitudeField: "",
  maxInlineActions: 3,
  mediaField: "",
  openOn: "event",
  pageBodyContent: { children: [{ actions: ["if", "@config.showActions", ["array/filter", "@config.actions", ["fn", "action", ["or", ["=", ["array/len", ["object/get", "@action", "roles", []]], 0], ["array/includes", ["object/get", "@action", "roles", []], "@config.viewerRole"]]]], []], avatarField: "@config.avatarField", closeEvent: "@config.closeEvent", coverField: "@config.coverField", entity: "@entity.loadedRow", fields: "@config.fields", initialData: "@entity.loadedRow", latitudeField: "@config.latitudeField", lineItemColumns: "@config.lineItemColumns", lineItems: ["object/get", "@entity.loadedRow", "@config.lineItemsField"], longitudeField: "@config.longitudeField", look: "@config.recordLook", maxInlineActions: "@config.maxInlineActions", mediaField: "@config.mediaField", sections: "@config.sections", showActions: "@config.showActions", stageField: "@config.stageField", stages: "@config.stages", subtitle: "@entity.description", tabs: "@config.tabs", thread: ["object/get", "@entity.loadedRow", "@config.threadField"], title: "@entity.name", totals: "@config.totals", type: "detail-panel" }], className: "w-full max-w-4xl", direction: "vertical", gap: "lg", type: "stack" },
  placeholder: "Start writing…",
  recordLook: "ledger",
  sections: [],
  showActions: false,
  slideOverBodyContent: { children: [{ actions: ["if", "@config.showActions", ["array/filter", "@config.actions", ["fn", "action", ["or", ["=", ["array/len", ["object/get", "@action", "roles", []]], 0], ["array/includes", ["object/get", "@action", "roles", []], "@config.viewerRole"]]]], []], avatarField: "@config.avatarField", closeEvent: "@config.closeEvent", coverField: "@config.coverField", entity: "@entity.loadedRow", fields: "@config.fields", initialData: "@entity.loadedRow", latitudeField: "@config.latitudeField", lineItemColumns: "@config.lineItemColumns", lineItems: ["object/get", "@entity.loadedRow", "@config.lineItemsField"], longitudeField: "@config.longitudeField", look: "@config.recordLook", maxInlineActions: "@config.maxInlineActions", mediaField: "@config.mediaField", sections: "@config.sections", showActions: "@config.showActions", slideOver: true, stageField: "@config.stageField", stages: "@config.stages", subtitle: "@entity.description", tabs: "@config.tabs", thread: ["object/get", "@entity.loadedRow", "@config.threadField"], title: "@entity.name", totals: "@config.totals", type: "detail-panel" }], direction: "vertical", gap: "none", type: "stack" },
  stageField: "",
  stages: [],
  subtitleField: "customer",
  tabs: [],
  threadField: "",
  titleField: "name",
  titleFields: [],
  totals: [{ format: "currency", header: ["i18n/t", "std-almadar-demo-invoices-page:totals.subtotal"], key: "subtotal" }, { format: "currency", header: ["i18n/t", "std-almadar-demo-invoices-page:totals.tax"], key: "tax" }, { format: "currency", header: ["i18n/t", "std-almadar-demo-invoices-page:totals.total"], key: "amount" }],
  viewerRole: "",
} as HomeARDemoInvoicePageDetailConfig;

export interface HomeARDemoInvoicePageDetailLogicReturn {
  state: HomeARDemoInvoicePageDetailState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: HomeARDemoInvoicePageDetailEvent | null;
  slotWriter: Partial<Record<HomeARDemoInvoicePageDetailSlot, string | null>>;
  loading: boolean;
  pendingEvent: HomeARDemoInvoicePageDetailEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeARDemoInvoicePageItem>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof HomeARDemoInvoicePageDetailEventPayloadMap]: (payload?: HomeARDemoInvoicePageDetailEventPayloadMap[K]) => Promise<EventResponse> };
  config: HomeARDemoInvoicePageDetailConfig;
}

const __CASCADE_EVENTS = new Set<string>(['INIT', 'OPEN_INVOICE', 'OPEN_VIEW', 'VIEW']);

const __LEG_EVENTS: ReadonlySet<string> = new Set(['INIT', 'OPEN_VIEW', 'RETRY']);

export function useHomeARDemoInvoicePageDetailLogic(config?: Partial<HomeARDemoInvoicePageDetailConfig>): HomeARDemoInvoicePageDetailLogicReturn {
  const mergedConfig: HomeARDemoInvoicePageDetailConfig = { ...DEFAULT_HOME_AR_DEMO_INVOICE_PAGE_DETAIL_CONFIG, ...(config ?? {}) };

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/home-ar-demo-invoice-page-detail', orbitalName: 'HomeAR', traitName: 'HomeARDemoInvoicePageDetail' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(homeARDemoInvoicePageDetailReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const cascadeReceivedRef = useRef<TraitStateSnapshot['cascadeReceived']>([]);

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  const { t: __t, locale: __locale } = useTranslate();
  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const payloadObj = payload;
    const entity: EntityRow[] = liveData['HomeARDemoInvoicePageItem'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};


    if (fromState === 'idle' && event === 'INIT' && (false)) {
      // idle → loading (on INIT)
        _fields['id'] = payloadObj?.id;
        fields = { ...(fields ?? {}), 'id': _fields['id'] } as Record<string, EventPayloadValue | undefined>;
        _fields['openDraft'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(payloadObj?.draft);
        fields = { ...(fields ?? {}), 'openDraft': _fields['openDraft'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'loading';
    } else if (fromState === 'idle' && event === 'INIT' && (false)) {
      // idle → loading (on INIT)
        _fields['slug'] = payloadObj?.slug;
        fields = { ...(fields ?? {}), 'slug': _fields['slug'] } as Record<string, EventPayloadValue | undefined>;
        _fields['openDraft'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(payloadObj?.draft);
        fields = { ...(fields ?? {}), 'openDraft': _fields['openDraft'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'loading';
    } else if (fromState === 'idle' && event === 'INIT' && (false)) {
      // idle → loading (on INIT)
        _fields['openDraft'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(payloadObj?.draft);
        fields = { ...(fields ?? {}), 'openDraft': _fields['openDraft'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'loading';
    } else if (fromState === 'idle' && event === 'INIT' && (true)) {
      // idle → idle (on INIT)
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'CONTENT_SAVED') {
      // idle → idle (on CONTENT_SAVED)
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'CONTENT_COMMITTED') {
      // idle → idle (on CONTENT_COMMITTED)
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'TITLE_COMMITTED') {
      // idle → idle (on TITLE_COMMITTED)
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'OPEN_VIEW') {
      // idle → loading (on OPEN_VIEW)
        _fields['id'] = payloadObj?.id;
        fields = { ...(fields ?? {}), 'id': _fields['id'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'loading';
    } else if (fromState === 'loading' && event === 'INIT' && ((((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(payloadObj?.id) !== ""))) {
      // loading → loading (on INIT)
        _fields['id'] = payloadObj?.id;
        fields = { ...(fields ?? {}), 'id': _fields['id'] } as Record<string, EventPayloadValue | undefined>;
        _fields['openDraft'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(payloadObj?.draft);
        fields = { ...(fields ?? {}), 'openDraft': _fields['openDraft'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'loading';
    } else if (fromState === 'loading' && event === 'INIT' && (((((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(payloadObj?.id) === "") && (((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(payloadObj?.slug) !== "")))) {
      // loading → loading (on INIT)
        _fields['slug'] = payloadObj?.slug;
        fields = { ...(fields ?? {}), 'slug': _fields['slug'] } as Record<string, EventPayloadValue | undefined>;
        _fields['openDraft'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(payloadObj?.draft);
        fields = { ...(fields ?? {}), 'openDraft': _fields['openDraft'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'loading';
    } else if (fromState === 'loading' && event === 'RecordItemLoaded' && (false)) {
      // loading → editing (on RecordItemLoaded)
        _fields['loadedRow'] = Object.assign({}, payloadObj?.data, {  });
        fields = { ...(fields ?? {}), 'loadedRow': _fields['loadedRow'] } as Record<string, EventPayloadValue | undefined>;
        _fields['id'] = getNestedValue(payloadObj?.data, "id");
        fields = { ...(fields ?? {}), 'id': _fields['id'] } as Record<string, EventPayloadValue | undefined>;
        _fields['name'] = ((([]).length > 0) ? ((([] as readonly EntityRow[])).map((f) => ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(getNestedValue(payloadObj?.data, String(f))))).join(" ") : getNestedValue(payloadObj?.data, "name"));
        fields = { ...(fields ?? {}), 'name': _fields['name'] } as Record<string, EventPayloadValue | undefined>;
        _fields['description'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(getNestedValue(payloadObj?.data, "customer"));
        fields = { ...(fields ?? {}), 'description': _fields['description'] } as Record<string, EventPayloadValue | undefined>;
        _fields['content'] = getNestedValue(payloadObj?.data, "content");
        fields = { ...(fields ?? {}), 'content': _fields['content'] } as Record<string, EventPayloadValue | undefined>;
        _fields['coverUrl'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(getNestedValue(payloadObj?.data, ""));
        fields = { ...(fields ?? {}), 'coverUrl': _fields['coverUrl'] } as Record<string, EventPayloadValue | undefined>;
        _fields['iconName'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(getNestedValue(payloadObj?.data, ""));
        fields = { ...(fields ?? {}), 'iconName': _fields['iconName'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'editing';
    } else if (fromState === 'loading' && event === 'RecordItemLoaded' && (true)) {
      // loading → viewing (on RecordItemLoaded)
        _fields['loadedRow'] = Object.assign({}, payloadObj?.data, {  });
        fields = { ...(fields ?? {}), 'loadedRow': _fields['loadedRow'] } as Record<string, EventPayloadValue | undefined>;
        _fields['id'] = getNestedValue(payloadObj?.data, "id");
        fields = { ...(fields ?? {}), 'id': _fields['id'] } as Record<string, EventPayloadValue | undefined>;
        _fields['name'] = ((([]).length > 0) ? ((([] as readonly EntityRow[])).map((f) => ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(getNestedValue(payloadObj?.data, String(f))))).join(" ") : getNestedValue(payloadObj?.data, "name"));
        fields = { ...(fields ?? {}), 'name': _fields['name'] } as Record<string, EventPayloadValue | undefined>;
        _fields['description'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(getNestedValue(payloadObj?.data, "customer"));
        fields = { ...(fields ?? {}), 'description': _fields['description'] } as Record<string, EventPayloadValue | undefined>;
        _fields['content'] = getNestedValue(payloadObj?.data, "content");
        fields = { ...(fields ?? {}), 'content': _fields['content'] } as Record<string, EventPayloadValue | undefined>;
        _fields['coverUrl'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(getNestedValue(payloadObj?.data, ""));
        fields = { ...(fields ?? {}), 'coverUrl': _fields['coverUrl'] } as Record<string, EventPayloadValue | undefined>;
        _fields['iconName'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(getNestedValue(payloadObj?.data, ""));
        fields = { ...(fields ?? {}), 'iconName': _fields['iconName'] } as Record<string, EventPayloadValue | undefined>;
        if (false) {

        } else {

          if (false) {

          } else {

            if (false) {

            } else {

            }

          }

        }

      _newState = 'viewing';
    } else if (fromState === 'loading' && event === 'RecordItemSlugLoaded' && (false)) {
      // loading → editing (on RecordItemSlugLoaded)
        _fields['loadedRow'] = (((payloadObj?.data as readonly EventPayloadValue[] | undefined) ?? []) as readonly EventPayloadValue[])[0];
        fields = { ...(fields ?? {}), 'loadedRow': _fields['loadedRow'] } as Record<string, EventPayloadValue | undefined>;
        _fields['id'] = getNestedValue((((payloadObj?.data as readonly EventPayloadValue[] | undefined) ?? []) as readonly EventPayloadValue[])[0], "id");
        fields = { ...(fields ?? {}), 'id': _fields['id'] } as Record<string, EventPayloadValue | undefined>;
        _fields['name'] = ((([]).length > 0) ? ((([] as readonly EntityRow[])).map((f) => ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(getNestedValue((((payloadObj?.data as readonly EventPayloadValue[] | undefined) ?? []) as readonly EventPayloadValue[])[0], String(f))))).join(" ") : getNestedValue((((payloadObj?.data as readonly EventPayloadValue[] | undefined) ?? []) as readonly EventPayloadValue[])[0], "name"));
        fields = { ...(fields ?? {}), 'name': _fields['name'] } as Record<string, EventPayloadValue | undefined>;
        _fields['description'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(getNestedValue((((payloadObj?.data as readonly EventPayloadValue[] | undefined) ?? []) as readonly EventPayloadValue[])[0], "customer"));
        fields = { ...(fields ?? {}), 'description': _fields['description'] } as Record<string, EventPayloadValue | undefined>;
        _fields['content'] = getNestedValue((((payloadObj?.data as readonly EventPayloadValue[] | undefined) ?? []) as readonly EventPayloadValue[])[0], "content");
        fields = { ...(fields ?? {}), 'content': _fields['content'] } as Record<string, EventPayloadValue | undefined>;
        _fields['coverUrl'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(getNestedValue((((payloadObj?.data as readonly EventPayloadValue[] | undefined) ?? []) as readonly EventPayloadValue[])[0], ""));
        fields = { ...(fields ?? {}), 'coverUrl': _fields['coverUrl'] } as Record<string, EventPayloadValue | undefined>;
        _fields['iconName'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(getNestedValue((((payloadObj?.data as readonly EventPayloadValue[] | undefined) ?? []) as readonly EventPayloadValue[])[0], ""));
        fields = { ...(fields ?? {}), 'iconName': _fields['iconName'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'editing';
    } else if (fromState === 'loading' && event === 'RecordItemSlugLoaded' && ((((payloadObj?.data ?? []) as { readonly length: number }).length > 0))) {
      // loading → viewing (on RecordItemSlugLoaded)
        _fields['loadedRow'] = (((payloadObj?.data as readonly EventPayloadValue[] | undefined) ?? []) as readonly EventPayloadValue[])[0];
        fields = { ...(fields ?? {}), 'loadedRow': _fields['loadedRow'] } as Record<string, EventPayloadValue | undefined>;
        _fields['id'] = getNestedValue((((payloadObj?.data as readonly EventPayloadValue[] | undefined) ?? []) as readonly EventPayloadValue[])[0], "id");
        fields = { ...(fields ?? {}), 'id': _fields['id'] } as Record<string, EventPayloadValue | undefined>;
        _fields['name'] = ((([]).length > 0) ? ((([] as readonly EntityRow[])).map((f) => ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(getNestedValue((((payloadObj?.data as readonly EventPayloadValue[] | undefined) ?? []) as readonly EventPayloadValue[])[0], String(f))))).join(" ") : getNestedValue((((payloadObj?.data as readonly EventPayloadValue[] | undefined) ?? []) as readonly EventPayloadValue[])[0], "name"));
        fields = { ...(fields ?? {}), 'name': _fields['name'] } as Record<string, EventPayloadValue | undefined>;
        _fields['description'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(getNestedValue((((payloadObj?.data as readonly EventPayloadValue[] | undefined) ?? []) as readonly EventPayloadValue[])[0], "customer"));
        fields = { ...(fields ?? {}), 'description': _fields['description'] } as Record<string, EventPayloadValue | undefined>;
        _fields['content'] = getNestedValue((((payloadObj?.data as readonly EventPayloadValue[] | undefined) ?? []) as readonly EventPayloadValue[])[0], "content");
        fields = { ...(fields ?? {}), 'content': _fields['content'] } as Record<string, EventPayloadValue | undefined>;
        _fields['coverUrl'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(getNestedValue((((payloadObj?.data as readonly EventPayloadValue[] | undefined) ?? []) as readonly EventPayloadValue[])[0], ""));
        fields = { ...(fields ?? {}), 'coverUrl': _fields['coverUrl'] } as Record<string, EventPayloadValue | undefined>;
        _fields['iconName'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(getNestedValue((((payloadObj?.data as readonly EventPayloadValue[] | undefined) ?? []) as readonly EventPayloadValue[])[0], ""));
        fields = { ...(fields ?? {}), 'iconName': _fields['iconName'] } as Record<string, EventPayloadValue | undefined>;
        if (false) {

        } else {

          if (false) {

          } else {

            if (false) {

            } else {

            }

          }

        }

      _newState = 'viewing';
    } else if (fromState === 'loading' && event === 'RecordItemSlugLoaded' && ((((payloadObj?.data ?? []) as { readonly length: number }).length === 0))) {
      // loading → error (on RecordItemSlugLoaded)
      _newState = 'error';
    } else if (fromState === 'loading' && event === 'RecordItemLoadFailed') {
      // loading → error (on RecordItemLoadFailed)
      _newState = 'error';
    } else if (fromState === 'editing' && event === 'INIT' && (((((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(payloadObj?.id) !== "") && (payloadObj?.id !== fields?.id)))) {
      // editing → loading (on INIT)
        _fields['id'] = payloadObj?.id;
        fields = { ...(fields ?? {}), 'id': _fields['id'] } as Record<string, EventPayloadValue | undefined>;
        _fields['openDraft'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(payloadObj?.draft);
        fields = { ...(fields ?? {}), 'openDraft': _fields['openDraft'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'loading';
    } else if (fromState === 'editing' && event === 'INIT' && ((((((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(payloadObj?.id) === "") && (((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(payloadObj?.slug) !== "")) && (payloadObj?.slug !== fields?.slug)))) {
      // editing → loading (on INIT)
        _fields['slug'] = payloadObj?.slug;
        fields = { ...(fields ?? {}), 'slug': _fields['slug'] } as Record<string, EventPayloadValue | undefined>;
        _fields['openDraft'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(payloadObj?.draft);
        fields = { ...(fields ?? {}), 'openDraft': _fields['openDraft'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'loading';
    } else if (fromState === 'editing' && event === 'CONTENT_CHANGE') {
      // editing → editing (on CONTENT_CHANGE)
        _fields['content'] = payloadObj?.value;
        fields = { ...(fields ?? {}), 'content': _fields['content'] } as Record<string, EventPayloadValue | undefined>;
        _fields['loadedRow'] = (((__src: EntityRow | EventPayload | FieldValue | EventPayloadValue | null | undefined, __path: string, __value: FieldValue | EventPayloadValue) => { type __Rec = Record<string, FieldValue | EventPayloadValue | undefined>; const __isRec = (__x: EntityRow | EventPayload | FieldValue | EventPayloadValue | undefined): __x is __Rec => __x !== null && typeof __x === 'object' && !Array.isArray(__x) && !(__x instanceof Date); if (__src !== null && __src !== undefined && !__isRec(__src)) { throw new Error('object/set: expected object, got ' + typeof __src); } const __o: __Rec = __src === null || __src === undefined ? {} : structuredClone(__src); let __c: __Rec = __o; const __ks = __path.split('.'); for (let __i = 0; __i < __ks.length - 1; __i += 1) { const __n = __c[__ks[__i]]; const __next: __Rec = __isRec(__n) ? __n : {}; __c[__ks[__i]] = __next; __c = __next; } __c[__ks[__ks.length - 1]] = __value; return __o; })(fields?.loadedRow, String("content"), payloadObj?.value));
        fields = { ...(fields ?? {}), 'loadedRow': _fields['loadedRow'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('CONTENT_SAVED', _p, { trait: 'HomeARDemoInvoicePageDetail' }); emitCascadeEvent(eventBus, 'HomeAR', 'HomeARDemoInvoicePageDetail', { event: 'CONTENT_SAVED', payload: _p, source: { orbital: 'HomeAR', trait: 'HomeARDemoInvoicePageDetail' } }); })({ content: fields?.content, id: fields?.id }));
      _newState = 'editing';
    } else if (fromState === 'editing' && event === 'TITLE_COMMITTED') {
      // editing → editing (on TITLE_COMMITTED)
        _fields['name'] = payloadObj?.title;
        fields = { ...(fields ?? {}), 'name': _fields['name'] } as Record<string, EventPayloadValue | undefined>;
        _fields['loadedRow'] = (((__src: EntityRow | EventPayload | FieldValue | EventPayloadValue | null | undefined, __path: string, __value: FieldValue | EventPayloadValue) => { type __Rec = Record<string, FieldValue | EventPayloadValue | undefined>; const __isRec = (__x: EntityRow | EventPayload | FieldValue | EventPayloadValue | undefined): __x is __Rec => __x !== null && typeof __x === 'object' && !Array.isArray(__x) && !(__x instanceof Date); if (__src !== null && __src !== undefined && !__isRec(__src)) { throw new Error('object/set: expected object, got ' + typeof __src); } const __o: __Rec = __src === null || __src === undefined ? {} : structuredClone(__src); let __c: __Rec = __o; const __ks = __path.split('.'); for (let __i = 0; __i < __ks.length - 1; __i += 1) { const __n = __c[__ks[__i]]; const __next: __Rec = __isRec(__n) ? __n : {}; __c[__ks[__i]] = __next; __c = __next; } __c[__ks[__ks.length - 1]] = __value; return __o; })(fields?.loadedRow, String("name"), payloadObj?.title));
        fields = { ...(fields ?? {}), 'loadedRow': _fields['loadedRow'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'editing';
    } else if (fromState === 'editing' && event === 'DONE_EDITING') {
      // editing → viewing (on DONE_EDITING)
        _emits.push(((_p) => () => { eventBus.emit('CONTENT_SAVED', _p, { trait: 'HomeARDemoInvoicePageDetail' }); emitCascadeEvent(eventBus, 'HomeAR', 'HomeARDemoInvoicePageDetail', { event: 'CONTENT_SAVED', payload: _p, source: { orbital: 'HomeAR', trait: 'HomeARDemoInvoicePageDetail' } }); })({ id: fields?.id, content: fields?.content }));
        _emits.push(((_p) => () => { eventBus.emit('CONTENT_COMMITTED', _p, { trait: 'HomeARDemoInvoicePageDetail' }); emitCascadeEvent(eventBus, 'HomeAR', 'HomeARDemoInvoicePageDetail', { event: 'CONTENT_COMMITTED', payload: _p, source: { orbital: 'HomeAR', trait: 'HomeARDemoInvoicePageDetail' } }); })({ content: fields?.content, id: fields?.id }));
      _newState = 'viewing';
    } else if (fromState === 'editing' && event === 'CLOSE_VIEW') {
      // editing → idle (on CLOSE_VIEW)
        _emits.push(((_p) => () => { eventBus.emit('CONTENT_SAVED', _p, { trait: 'HomeARDemoInvoicePageDetail' }); emitCascadeEvent(eventBus, 'HomeAR', 'HomeARDemoInvoicePageDetail', { event: 'CONTENT_SAVED', payload: _p, source: { orbital: 'HomeAR', trait: 'HomeARDemoInvoicePageDetail' } }); })({ id: fields?.id, content: fields?.content }));
      _newState = 'idle';
    } else if (fromState === 'viewing' && event === 'INIT' && ((((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(payloadObj?.id) !== ""))) {
      // viewing → loading (on INIT)
        _fields['id'] = payloadObj?.id;
        fields = { ...(fields ?? {}), 'id': _fields['id'] } as Record<string, EventPayloadValue | undefined>;
        _fields['openDraft'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(payloadObj?.draft);
        fields = { ...(fields ?? {}), 'openDraft': _fields['openDraft'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'loading';
    } else if (fromState === 'viewing' && event === 'INIT' && (((((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(payloadObj?.id) === "") && (((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(payloadObj?.slug) !== "")))) {
      // viewing → loading (on INIT)
        _fields['slug'] = payloadObj?.slug;
        fields = { ...(fields ?? {}), 'slug': _fields['slug'] } as Record<string, EventPayloadValue | undefined>;
        _fields['openDraft'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(payloadObj?.draft);
        fields = { ...(fields ?? {}), 'openDraft': _fields['openDraft'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'loading';
    } else if (fromState === 'viewing' && event === 'RecordItemLoaded') {
      // viewing → viewing (on RecordItemLoaded)
        _fields['loadedRow'] = Object.assign({}, payloadObj?.data, {  });
        fields = { ...(fields ?? {}), 'loadedRow': _fields['loadedRow'] } as Record<string, EventPayloadValue | undefined>;
        _fields['id'] = getNestedValue(payloadObj?.data, "id");
        fields = { ...(fields ?? {}), 'id': _fields['id'] } as Record<string, EventPayloadValue | undefined>;
        _fields['name'] = ((([]).length > 0) ? ((([] as readonly EntityRow[])).map((f) => ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(getNestedValue(payloadObj?.data, String(f))))).join(" ") : getNestedValue(payloadObj?.data, "name"));
        fields = { ...(fields ?? {}), 'name': _fields['name'] } as Record<string, EventPayloadValue | undefined>;
        _fields['description'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(getNestedValue(payloadObj?.data, "customer"));
        fields = { ...(fields ?? {}), 'description': _fields['description'] } as Record<string, EventPayloadValue | undefined>;
        _fields['content'] = getNestedValue(payloadObj?.data, "content");
        fields = { ...(fields ?? {}), 'content': _fields['content'] } as Record<string, EventPayloadValue | undefined>;
        _fields['coverUrl'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(getNestedValue(payloadObj?.data, ""));
        fields = { ...(fields ?? {}), 'coverUrl': _fields['coverUrl'] } as Record<string, EventPayloadValue | undefined>;
        _fields['iconName'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(getNestedValue(payloadObj?.data, ""));
        fields = { ...(fields ?? {}), 'iconName': _fields['iconName'] } as Record<string, EventPayloadValue | undefined>;
        if (false) {

        } else {

          if (false) {

          } else {

            if (false) {

            } else {

            }

          }

        }

      _newState = 'viewing';
    } else if (fromState === 'viewing' && event === 'RecordItemSlugLoaded' && ((((payloadObj?.data ?? []) as { readonly length: number }).length > 0))) {
      // viewing → viewing (on RecordItemSlugLoaded)
        _fields['loadedRow'] = (((payloadObj?.data as readonly EventPayloadValue[] | undefined) ?? []) as readonly EventPayloadValue[])[0];
        fields = { ...(fields ?? {}), 'loadedRow': _fields['loadedRow'] } as Record<string, EventPayloadValue | undefined>;
        _fields['id'] = getNestedValue((((payloadObj?.data as readonly EventPayloadValue[] | undefined) ?? []) as readonly EventPayloadValue[])[0], "id");
        fields = { ...(fields ?? {}), 'id': _fields['id'] } as Record<string, EventPayloadValue | undefined>;
        _fields['name'] = ((([]).length > 0) ? ((([] as readonly EntityRow[])).map((f) => ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(getNestedValue((((payloadObj?.data as readonly EventPayloadValue[] | undefined) ?? []) as readonly EventPayloadValue[])[0], String(f))))).join(" ") : getNestedValue((((payloadObj?.data as readonly EventPayloadValue[] | undefined) ?? []) as readonly EventPayloadValue[])[0], "name"));
        fields = { ...(fields ?? {}), 'name': _fields['name'] } as Record<string, EventPayloadValue | undefined>;
        _fields['description'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(getNestedValue((((payloadObj?.data as readonly EventPayloadValue[] | undefined) ?? []) as readonly EventPayloadValue[])[0], "customer"));
        fields = { ...(fields ?? {}), 'description': _fields['description'] } as Record<string, EventPayloadValue | undefined>;
        _fields['content'] = getNestedValue((((payloadObj?.data as readonly EventPayloadValue[] | undefined) ?? []) as readonly EventPayloadValue[])[0], "content");
        fields = { ...(fields ?? {}), 'content': _fields['content'] } as Record<string, EventPayloadValue | undefined>;
        _fields['coverUrl'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(getNestedValue((((payloadObj?.data as readonly EventPayloadValue[] | undefined) ?? []) as readonly EventPayloadValue[])[0], ""));
        fields = { ...(fields ?? {}), 'coverUrl': _fields['coverUrl'] } as Record<string, EventPayloadValue | undefined>;
        _fields['iconName'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(getNestedValue((((payloadObj?.data as readonly EventPayloadValue[] | undefined) ?? []) as readonly EventPayloadValue[])[0], ""));
        fields = { ...(fields ?? {}), 'iconName': _fields['iconName'] } as Record<string, EventPayloadValue | undefined>;
        if (false) {

        } else {

          if (false) {

          } else {

            if (false) {

            } else {

            }

          }

        }

      _newState = 'viewing';
    } else if (fromState === 'viewing' && event === 'RecordItemSlugLoaded' && ((((payloadObj?.data ?? []) as { readonly length: number }).length === 0))) {
      // viewing → error (on RecordItemSlugLoaded)
      _newState = 'error';
    } else if (fromState === 'viewing' && event === 'EDIT_CONTENT' && (false)) {
      // viewing → editing (on EDIT_CONTENT)
      _newState = 'editing';
    } else if (fromState === 'viewing' && event === 'TITLE_COMMITTED') {
      // viewing → viewing (on TITLE_COMMITTED)
        _fields['name'] = payloadObj?.title;
        fields = { ...(fields ?? {}), 'name': _fields['name'] } as Record<string, EventPayloadValue | undefined>;
        _fields['loadedRow'] = (((__src: EntityRow | EventPayload | FieldValue | EventPayloadValue | null | undefined, __path: string, __value: FieldValue | EventPayloadValue) => { type __Rec = Record<string, FieldValue | EventPayloadValue | undefined>; const __isRec = (__x: EntityRow | EventPayload | FieldValue | EventPayloadValue | undefined): __x is __Rec => __x !== null && typeof __x === 'object' && !Array.isArray(__x) && !(__x instanceof Date); if (__src !== null && __src !== undefined && !__isRec(__src)) { throw new Error('object/set: expected object, got ' + typeof __src); } const __o: __Rec = __src === null || __src === undefined ? {} : structuredClone(__src); let __c: __Rec = __o; const __ks = __path.split('.'); for (let __i = 0; __i < __ks.length - 1; __i += 1) { const __n = __c[__ks[__i]]; const __next: __Rec = __isRec(__n) ? __n : {}; __c[__ks[__i]] = __next; __c = __next; } __c[__ks[__ks.length - 1]] = __value; return __o; })(fields?.loadedRow, String("name"), payloadObj?.title));
        fields = { ...(fields ?? {}), 'loadedRow': _fields['loadedRow'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'viewing';
    } else if (fromState === 'viewing' && event === 'RecordItemLoadFailed') {
      // viewing → error (on RecordItemLoadFailed)
      _newState = 'error';
    } else if (fromState === 'viewing' && event === 'CLOSE_VIEW') {
      // viewing → idle (on CLOSE_VIEW)
      _newState = 'idle';
    } else if (fromState === 'error' && event === 'INIT' && ((((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(payloadObj?.id) !== ""))) {
      // error → loading (on INIT)
        _fields['id'] = payloadObj?.id;
        fields = { ...(fields ?? {}), 'id': _fields['id'] } as Record<string, EventPayloadValue | undefined>;
        _fields['openDraft'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(payloadObj?.draft);
        fields = { ...(fields ?? {}), 'openDraft': _fields['openDraft'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'loading';
    } else if (fromState === 'error' && event === 'INIT' && (((((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(payloadObj?.id) === "") && (((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(payloadObj?.slug) !== "")))) {
      // error → loading (on INIT)
        _fields['slug'] = payloadObj?.slug;
        fields = { ...(fields ?? {}), 'slug': _fields['slug'] } as Record<string, EventPayloadValue | undefined>;
        _fields['openDraft'] = ((v) => (v == null || (typeof v === 'string' && v === '') ? "" : v))(payloadObj?.draft);
        fields = { ...(fields ?? {}), 'openDraft': _fields['openDraft'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'loading';
    } else if (fromState === 'error' && event === 'RETRY') {
      // error → loading (on RETRY)
      _newState = 'loading';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: HomeARDemoInvoicePageDetailSlot[]; clear: HomeARDemoInvoicePageDetailSlot[] } => {
    switch (fromState) {
      case 'editing':
        switch (event) {
          case 'CLOSE_VIEW': return { write: [] as HomeARDemoInvoicePageDetailSlot[], clear: ['main'] as HomeARDemoInvoicePageDetailSlot[] };
          case 'DONE_EDITING': return { write: ['main'] as HomeARDemoInvoicePageDetailSlot[], clear: [] as HomeARDemoInvoicePageDetailSlot[] };
          case 'INIT': return { write: ['main'] as HomeARDemoInvoicePageDetailSlot[], clear: [] as HomeARDemoInvoicePageDetailSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'error':
        switch (event) {
          case 'INIT': return { write: ['main'] as HomeARDemoInvoicePageDetailSlot[], clear: [] as HomeARDemoInvoicePageDetailSlot[] };
          case 'RETRY': return { write: ['main'] as HomeARDemoInvoicePageDetailSlot[], clear: [] as HomeARDemoInvoicePageDetailSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'idle':
        switch (event) {
          case 'INIT': return { write: ['main'] as HomeARDemoInvoicePageDetailSlot[], clear: ['main'] as HomeARDemoInvoicePageDetailSlot[] };
          case 'OPEN_VIEW': return { write: ['main'] as HomeARDemoInvoicePageDetailSlot[], clear: [] as HomeARDemoInvoicePageDetailSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'loading':
        switch (event) {
          case 'INIT': return { write: ['main'] as HomeARDemoInvoicePageDetailSlot[], clear: [] as HomeARDemoInvoicePageDetailSlot[] };
          case 'RecordItemLoadFailed': return { write: ['main'] as HomeARDemoInvoicePageDetailSlot[], clear: [] as HomeARDemoInvoicePageDetailSlot[] };
          case 'RecordItemLoaded': return { write: ['main'] as HomeARDemoInvoicePageDetailSlot[], clear: [] as HomeARDemoInvoicePageDetailSlot[] };
          case 'RecordItemSlugLoaded': return { write: ['main'] as HomeARDemoInvoicePageDetailSlot[], clear: [] as HomeARDemoInvoicePageDetailSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'viewing':
        switch (event) {
          case 'CLOSE_VIEW': return { write: [] as HomeARDemoInvoicePageDetailSlot[], clear: ['main'] as HomeARDemoInvoicePageDetailSlot[] };
          case 'EDIT_CONTENT': return { write: ['main'] as HomeARDemoInvoicePageDetailSlot[], clear: [] as HomeARDemoInvoicePageDetailSlot[] };
          case 'RecordItemLoadFailed': return { write: ['main'] as HomeARDemoInvoicePageDetailSlot[], clear: [] as HomeARDemoInvoicePageDetailSlot[] };
          case 'RecordItemLoaded': return { write: ['main'] as HomeARDemoInvoicePageDetailSlot[], clear: [] as HomeARDemoInvoicePageDetailSlot[] };
          case 'RecordItemSlugLoaded': return { write: ['main'] as HomeARDemoInvoicePageDetailSlot[], clear: [] as HomeARDemoInvoicePageDetailSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'HomeARDemoInvoicePageItem': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as HomeARDemoInvoicePageDetailEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    if (__CASCADE_EVENTS.has(event)) {
      cascadeReceivedRef.current.push({ event, ...(payload !== undefined ? { payload } : {}), timestamp: Date.now() });
    }
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    if (__LEG_EVENTS.has(event)) {
    // Server leg: ordered by the pump, never blocking the drain.
    commandPump.enqueue(async () => {
      try {
        const response = await sendEvent(event, payload, prevState);
        if (response.success) {
          // Agreement — merge any fetch/persist results the local commit
          // couldn't supply (same reducer action, deferred leg).
          if (response.data && Object.keys(response.data).length > 0) {
            dispatchReducer({
              type: 'EVENT_SUCCESS',
              event: event as HomeARDemoInvoicePageDetailEvent,
              newState: machineStateRef.current,
              data: response.data,
              renderBearing: false,
            });
          }
        } else {
          // Rejection or state disagreement — roll back to the
          // pre-dispatch snapshot; the server stays authoritative.
          dispatchReducer({
            type: 'EVENT_SUCCESS',
            event: event as HomeARDemoInvoicePageDetailEvent,
            newState: prevState,
            data: {},
            fields: _snapshot,
            renderBearing: false,
          });
          machineStateRef.current = prevState;
          fieldsRef.current = { ...fieldsRef.current, ..._snapshot } as EntityRow;
          dispatchReducer({ type: 'EVENT_ERROR', error: response.error ?? `server state mismatch: expected ${_optimistic.newState}, got ${response.states?.['HomeARDemoInvoicePageDetail'] ?? '(unchanged)'}` });
        }
      } catch (err) {
        // Pump isolates the rejection; surface it without rolling
        // back — local state may simply be ahead of a reconnecting server.
        dispatchReducer({ type: 'EVENT_ERROR', error: err instanceof Error ? err.message : 'Network error' });
      }
    });
    }
    return { success: true, transitioned: true, states: { 'HomeARDemoInvoicePageDetail': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof HomeARDemoInvoicePageDetailEventPayloadMap]: (payload?: HomeARDemoInvoicePageDetailEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': (payload?: HomeARDemoInvoicePageDetailEventPayloadMap['INIT']) => dispatch('INIT', payload),
    'CONTENT_SAVED': (payload?: HomeARDemoInvoicePageDetailEventPayloadMap['CONTENT_SAVED']) => dispatch('CONTENT_SAVED', payload),
    'CONTENT_COMMITTED': (payload?: HomeARDemoInvoicePageDetailEventPayloadMap['CONTENT_COMMITTED']) => dispatch('CONTENT_COMMITTED', payload),
    'TITLE_COMMITTED': (payload?: HomeARDemoInvoicePageDetailEventPayloadMap['TITLE_COMMITTED']) => dispatch('TITLE_COMMITTED', payload),
    'OPEN_VIEW': (payload?: HomeARDemoInvoicePageDetailEventPayloadMap['OPEN_VIEW']) => dispatch('OPEN_VIEW', payload),
    'RecordItemLoaded': (payload?: HomeARDemoInvoicePageDetailEventPayloadMap['RecordItemLoaded']) => dispatch('RecordItemLoaded', payload),
    'RecordItemSlugLoaded': (payload?: HomeARDemoInvoicePageDetailEventPayloadMap['RecordItemSlugLoaded']) => dispatch('RecordItemSlugLoaded', payload),
    'RecordItemLoadFailed': (payload?: HomeARDemoInvoicePageDetailEventPayloadMap['RecordItemLoadFailed']) => dispatch('RecordItemLoadFailed', payload),
    'CONTENT_CHANGE': (payload?: HomeARDemoInvoicePageDetailEventPayloadMap['CONTENT_CHANGE']) => dispatch('CONTENT_CHANGE', payload),
    'DONE_EDITING': () => dispatch('DONE_EDITING'),
    'CLOSE_VIEW': () => dispatch('CLOSE_VIEW'),
    'EDIT_CONTENT': () => dispatch('EDIT_CONTENT'),
    'RETRY': () => dispatch('RETRY'),
    'EDIT': (payload?: HomeARDemoInvoicePageDetailEventPayloadMap['EDIT']) => dispatch('EDIT', payload),
    'DELETE': (payload?: HomeARDemoInvoicePageDetailEventPayloadMap['DELETE']) => dispatch('DELETE', payload),
  };

  useUIEvents(enqueueEvent, 'HomeAR.HomeARDemoInvoicePageDetail', ['INIT', 'CONTENT_SAVED', 'CONTENT_COMMITTED', 'TITLE_COMMITTED', 'OPEN_VIEW', 'RecordItemLoaded', 'RecordItemSlugLoaded', 'RecordItemLoadFailed', 'CONTENT_CHANGE', 'DONE_EDITING', 'CLOSE_VIEW', 'EDIT_CONTENT', 'RETRY'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('HomeARDemoInvoicePageDetail', (): TraitStateSnapshot => ({
      traitName: 'HomeARDemoInvoicePageDetail',
      currentState: internalStateRef.current.machineState,
      states: ['idle', 'loading', 'editing', 'viewing', 'error'],
      events: ['INIT', 'CONTENT_SAVED', 'CONTENT_COMMITTED', 'TITLE_COMMITTED', 'OPEN_VIEW', 'RecordItemLoaded', 'RecordItemSlugLoaded', 'RecordItemLoadFailed', 'CONTENT_CHANGE', 'DONE_EDITING', 'CLOSE_VIEW', 'EDIT_CONTENT', 'RETRY', 'EDIT', 'DELETE'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: cascadeReceivedRef.current,
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-trait event listeners (gap #13: qualified bus keys)
  useEffect(() => {
    // Listen for 'OPEN_INVOICE' (from HomeARDemoInvoicePageDesk) → trigger 'OPEN_VIEW'
    const unsub_open_invoice_from_homeardemoinvoicepagedesk_to_open_view = eventBus.on('UI:HomeAR.HomeARDemoInvoicePageDesk.OPEN_INVOICE', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'HomeAR.HomeARDemoInvoicePageDetail', 'OPEN_VIEW')) return;
      enqueueEvent('OPEN_VIEW', { id: (event.payload as EventPayload | undefined)?.id, row: { id: (event.payload as EventPayload | undefined)?.id } } as EventPayload);
    });
    // Listen for 'OPEN_INVOICE' (from HomeARDemoInvoicePageDesk) → trigger 'INIT'
    const unsub_open_invoice_from_homeardemoinvoicepagedesk_to_init = eventBus.on('UI:HomeAR.HomeARDemoInvoicePageDesk.OPEN_INVOICE', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'HomeAR.HomeARDemoInvoicePageDetail', 'INIT')) return;
      enqueueEvent('INIT', event.payload);
    });
    // Listen for 'VIEW' (from HomeARDemoInvoicePageList) → trigger 'INIT'
    const unsub_view_from_homeardemoinvoicepagelist_to_init = eventBus.on('UI:HomeAR.HomeARDemoInvoicePageList.VIEW', (event: BusEvent) => {
      if (!event.source || !(event.source as { fromBridge?: boolean }).fromBridge) {
        return;
      }
      if (!claimDelivery(event.source, 'HomeAR.HomeARDemoInvoicePageDetail', 'INIT')) return;
      enqueueEvent('INIT', { id: (event.payload as EventPayload | undefined)?.id } as EventPayload);
    });
    return () => {
      if (typeof unsub_open_invoice_from_homeardemoinvoicepagedesk_to_open_view === 'function') unsub_open_invoice_from_homeardemoinvoicepagedesk_to_open_view();
      if (typeof unsub_open_invoice_from_homeardemoinvoicepagedesk_to_init === 'function') unsub_open_invoice_from_homeardemoinvoicepagedesk_to_init();
      if (typeof unsub_view_from_homeardemoinvoicepagelist_to_init === 'function') unsub_view_from_homeardemoinvoicepagelist_to_init();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus, dispatch]);

  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
